/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * One invitation on My profile › Invitations. A received invitation says who
 * it is from and what accepting means BEFORE Accept and Decline (LT-2005).
 */
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Badge, Button, PrivateIcon, Spinner } from "@lemonadetraining/web-ui";

/* Own Modules */
import AthleteAvatar from "../AthleteAvatar";
import { InviteStatus, Invite } from "@lemonadetraining/contracts";
import {
  loadProgrammeWithUID,
  loadCoachProductWithUID,
  isProgrammeReadDenied,
} from "@lemonadetraining/web-shared";
import {
  acceptOrDeclineInvitation,
  refreshAuthToken,
  AuthContext,
} from "@lemonadetraining/web-shared";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import ProductCard from "@lemonadetraining/web-shared/components/ProductCard";
import { getBox } from "@lemonadetraining/web-shared";
import { formatCommitmentDate, toDate } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import { ROLE_EXPLAINERS } from "../../lib/roleExplainers";
import { callableMessage } from "../../lib/callableError";
import { PendingDate } from "../time/PendingDate";

interface InvitationViewProps {
  invite: Invite;
  direction: "sent" | "received";
  /** Called after an accept or decline lands; the buttons stay off until it settles. */
  onActed?: () => Promise<void> | void;
  /** The zone the viewer reads dates in: undefined while it loads, null = the browser's. */
  timeZone: string | null | undefined;
}

interface BoxData {
  name?: string;
  description?: string;
}

interface ProductData {
  id: string;
  data: React.ComponentProps<typeof ProductCard>["data"];
}

const STATUS_LABEL: Record<InviteStatus, string> = {
  Pending: "Waiting",
  Accepted: "Accepted",
  Declined: "Declined",
  Expired: "No longer valid",
};

const roleText = (role: string) => ROLE_EXPLAINERS.find((r) => r.role === role)?.text ?? "";

const InvitationView: React.FC<InvitationViewProps> = ({
  invite,
  direction,
  onActed,
  timeZone,
}) => {
  const dispatch = useDispatch();
  const { updateCustomClaims } = useContext(AuthContext);
  const [product, setProduct] = useState<ProductData | null>(null);
  const [productIsPrivate, setProductIsPrivate] = useState(false);
  const [box, setBox] = useState<BoxData | null>(null);
  const [loading, setLoading] = useState(false);
  const isBoxInvitation = invite.invitationType === "toBox";
  const pending = invite.status === "Pending";
  const expired = invite.status === "Expired";
  const sentOn = toDate(invite.createdAt);

  useEffect(() => {
    if (isBoxInvitation && invite.boxID) {
      getBox(invite.boxID)
        .then((boxData: unknown) => setBox(boxData as BoxData))
        .catch((error: Error) => console.log("Can't load the box]->", error.message));
      return;
    }
    if (invite.product?.type === "ProgrammingsProducts" && invite.product.id) {
      loadProgrammeWithUID(invite.product.id)
        .then((newData: unknown) => setProduct(newData as ProductData))
        .catch((error: Error) => {
          if (isProgrammeReadDenied(error)) {
            setProductIsPrivate(true);
            return;
          }
          console.log("Can't load the product]->", error.message);
        });
    }
    if (invite.product?.type === "CoachingSubscriptionProducts" && invite.product.id) {
      loadCoachProductWithUID(invite.product.id)
        .then((newData: unknown) => setProduct(newData as ProductData))
        .catch((error: Error) => console.log("Can't load the product]->", error.message));
    }
  }, [invite.product?.id, invite.boxID, isBoxInvitation, invite.product?.type]);

  // A list that fails to reload must not read as the invitation itself failing.
  const refreshList = async () => {
    try {
      await onActed?.();
    } catch (refreshError) {
      console.error("Invitations list did not reload:", refreshError);
    }
  };

  const onAcceptOrDecline = (status: InviteStatus) => {
    setLoading(true);
    acceptOrDeclineInvitation(invite.id, status)
      .then(
        async () => {
          // Accepting a staff invite changes custom claims; the client needs a fresh token.
          if (status === "Accepted" && isBoxInvitation) {
            try {
              await refreshAuthToken();
              await updateCustomClaims();
            } catch (tokenError) {
              console.error("Error refreshing auth token after box invitation:", tokenError);
            }
          }
          dispatch(
            notifyActions.sendNotification({
              severity: NOTIFICATION_TYPE.success,
              title: status === "Accepted" ? "Invitation accepted" : "Invitation declined",
              message:
                status === "Accepted"
                  ? "You're in. It may take a moment to show everywhere."
                  : "It no longer shows as waiting for you.",
            })
          );
          await refreshList();
        },
        async (error: unknown) => {
          dispatch(
            notifyActions.sendNotification({
              severity: NOTIFICATION_TYPE.error,
              title: "Not updated",
              message:
                callableMessage(error) || "We couldn't update this invitation. Please try again.",
            })
          );
          await refreshList();
        }
      )
      .finally(() => setLoading(false));
  };

  const statusNote = !expired
    ? null
    : direction === "received"
      ? "This invite is no longer valid. Ask the gym to send a new invite."
      : "This invite is no longer valid";

  const person =
    direction === "sent"
      ? { uid: invite.recipientID, name: invite.recipientName || invite.recipientEmail }
      : { uid: invite.senderID, name: invite.senderName || "Someone" };

  const roleLabel = invite.boxRole === "admin" ? "Admin" : "Coach";
  // Another gym's document is not readable before accepting, so the title the
  // server wrote ("Invitation to join <gym> as Coach") stands in for its name.
  const heading =
    direction === "received" && isBoxInvitation && box?.name
      ? `Join ${box.name}'s team as ${invite.boxRole === "admin" ? "an admin" : "a coach"}`
      : invite.title || "Invitation";

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border px-4 py-3"
      data-testid={`invitation-${direction}-${invite.id}`}
    >
      <div className="flex items-start gap-3">
        <AthleteAvatar AthleteUID={person.uid} Name={person.name} showName={false} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium">
            {direction === "sent" ? person.name : heading}
          </span>
          <span className="text-xs text-muted-foreground">
            {direction === "sent" ? heading : `From ${person.name}`}
            {sentOn ? (
              <>
                {" · "}
                {timeZone === undefined ? <PendingDate /> : formatCommitmentDate(sentOn, timeZone)}
              </>
            ) : null}
          </span>
          {statusNote && <span className="text-xs text-muted-foreground">{statusNote}</span>}
        </div>
        {!(direction === "received" && pending) && (
          <Badge
            variant={invite.status === "Accepted" ? "default" : "outline"}
            className="shrink-0"
          >
            {STATUS_LABEL[invite.status] ?? invite.status}
          </Badge>
        )}
      </div>

      {invite.message && <p className="text-sm">{invite.message}</p>}

      {isBoxInvitation && (
        <div className="flex flex-col gap-2 text-sm" data-testid="invitation-role">
          {box?.name && direction === "sent" && <span className="font-medium">{box.name}</span>}
          <dl className="flex flex-col gap-1">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">Role</dt>
              <dd className="font-medium">{roleLabel}</dd>
            </div>
            <dd>{roleText(roleLabel)}</dd>
            {invite.boxRole !== "admin" && invite.boxPermissions && (
              <dd data-testid="invitation-programmes">
                {invite.boxPermissions.canCreatePrograms
                  ? "Can create programmes."
                  : "Can't create programmes."}
              </dd>
            )}
          </dl>
          {direction === "received" && (
            <span className="text-xs text-muted-foreground">
              Your own training and bookings stay the same.
            </span>
          )}
        </div>
      )}

      {!isBoxInvitation && productIsPrivate && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <PrivateIcon className="size-4" aria-hidden="true" />
          Private PT programme
        </p>
      )}
      {!isBoxInvitation && product && (
        <ProductCard
          id={product.id}
          data={product.data}
          showAction={invite.status === "Accepted"}
        />
      )}

      {direction === "received" && pending && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="min-h-[44px] px-4"
            onClick={() => onAcceptOrDecline("Declined")}
            disabled={loading}
          >
            Decline
          </Button>
          <Button
            className="min-h-[44px] px-4"
            onClick={() => onAcceptOrDecline("Accepted")}
            disabled={loading}
            aria-busy={loading}
          >
            {loading && <Spinner className="h-4 w-4" />}
            Accept
          </Button>
        </div>
      )}
    </div>
  );
};

export default InvitationView;
